/**
 * Copy for the sign-in landing page — see the module header on
 * `SignInLanding.tsx` for why each app owns its own.
 *
 * Voice: calm, competent, procedural, per `design/design-system/readme.md`.
 * Sentence case throughout.
 */
export const SIGN_IN_LANDING_COPY = {
  headerStatus: 'Secure file exchange',
  title: {
    /**
     * The hero's heading for a visitor with no name to greet.
     *
     * **It used to be `APP_COPY.product`, with this sentence as a tagline
     * underneath it in body text.** The page header says the product name two
     * inches above the hero, so the heading was spending the largest type on
     * the screen repeating it — and the one line that says what the product
     * actually does was the smallest thing in the block. They swapped.
     *
     * No full stop, unlike the tagline it replaced: this is a headline now,
     * and a full stop at `text-display` reads as a typo rather than a sentence.
     */
    default: 'Secure file sharing between agencies',
    welcome: (displayName: string) => `Welcome back, ${displayName}`,
  },

  /**
   * The button's own label per state — this page's only feedback mechanism.
   * No spinner, no separate status line: the one control on the screen says
   * what is happening.
   *
   * **The labels name the credential now, and that reverses ADR-0050** (see
   * ADR-0051). That decision collapsed two buttons into one on the argument that
   * which credential you are about to present is not a choice anybody is being
   * asked to make, and left the icon as the only difference. It was right about
   * the choice and wrong about the consequence: this page now has four different
   * acts behind one control — sign in with a code, sign in with a passkey, add a
   * passkey, continue — and a single word covering all four means the button
   * cannot be its own status line, which is the rule everything else here rests
   * on. The icon still agrees with the label; it is no longer carrying it alone.
   */
  button: {
    checking: 'Checking your session…',
    /** The SYS-7 passcode. Named "email" because that is the thing the person does next. */
    email: 'Sign in with email',
    /**
     * This build can sign nobody in, and the button says so instead of trying.
     *
     * **A two-origin build with no other arrangement for the session** — the
     * SPA and the API on different registrable domains, so no cookie can exist.
     * The wording says "this build" rather than naming an environment, which is
     * what keeps it true: whoever reads it is a developer looking at a build
     * they made, and the mechanism is the fact that helps them. A person outside
     * such a build never sees it.
     *
     * **No environment reaches this today** (ADR-0059) — deployed dev did until
     * it began carrying the session handle itself. It stays because it is still
     * the right answer for a two-origin build without `VITE_SIMULATED_AUTH`,
     * which is what a new environment starts as.
     */
    unavailable: 'Sign-in is unavailable in this build',
    /**
     * No admin has authorised a credential for this account (USR-14).
     *
     * **The remedy is a person, which is the whole difference from
     * `unavailable` above.** That one names a build, because the reader is a
     * developer looking at something they made. This one names an
     * administrator, because the reader is an officer who cannot get in and the
     * only thing that helps them is knowing who to ask.
     *
     * **It says "ask" rather than describing the mechanism.** Nothing in this
     * product tells the admin on the person's behalf (ADR-0040), so the
     * sentence has to be an instruction and not a status — "enrollment is
     * locked" would be accurate and would leave somebody staring at it.
     */
    locked: 'Ask your agency administrator to unlock enrollment',
    passkey: 'Sign in with a passkey',
    /**
     * Enrolment, offered on the landing page rather than behind a redirect to
     * the account screen (ADR-0051). For a restricted session this is the first
     * of SYS-10's two acts, and the button becomes `passkey` the moment it
     * succeeds — the second act is the next click, on the same control.
     */
    enrol: 'Add a passkey',
    continue: 'Continue to Blue Partner',
    asserting: 'Waiting for your passkey…',
    /**
     * Distinct from `asserting` because the gesture is distinct: enrolling asks
     * the operating system to *create* something, which on Windows Hello is a
     * different dialog with a different prompt from the one that signs you in.
     */
    enrolling: 'Waiting for your device…',
    /**
     * One string for every failure cause: naming which part of the handshake
     * failed tells an attacker more than
     * it tells the user, and the next step is identical either way.
     */
    failed: 'Sign-in failed — try again',
    /**
     * A failed assertion, and the two ways out of one.
     *
     * **The page still does not navigate on the failure itself.** A cancelled
     * prompt and a credential this browser does not hold raise the identical
     * `NotAllowedError` — WebAuthn collapses them on purpose so the API cannot
     * be probed — so redirecting on the guess would send somebody who dismissed
     * a system sheet to Entra and their inbox, with no way back. What changes is
     * the button under their cursor: it stops offering the passkey and offers
     * the next credential instead, in one click they choose to take.
     *
     * **Which next credential depends on who they are**, and that is why there
     * are two strings rather than one. Somebody anonymous goes to the passcode.
     * Somebody already holding a restricted session came *from* the passcode, so
     * sending them back would be a loop — their way out is USR-11a's rung 1, a
     * replacement credential enrolled here and then asserted.
     */
    assertFailedEmail: 'That did not work — sign in with email',
    assertFailedEnrol: 'That did not work — add a passkey',
    enrolFailed: 'Could not add a passkey — try again',
  },

  /**
   * The enrolment nudge's way past, for a full session only.
   *
   * A restricted session is never shown this: it has nothing to be deferred to,
   * because the app refuses every surface it would reach (SYS-10). What was a
   * whole interstitial screen with two buttons — `SecureSignInPrompt` — is this
   * line of text under the primary control now.
   */
  defer: 'Not now',

  /**
   * The other credential, one click under the passkey button (ADR-0083).
   *
   * **"code" rather than "OTP", and "email" rather than "passcode".** The two
   * nouns this product uses for the thing are the ones the person will actually
   * meet: an email arrives, and it contains a code. `button.email` — "Sign in
   * with email" — names the same act from the primary position, and this reads as
   * its quieter sibling rather than as a different feature.
   *
   * **"instead" is doing work.** The button above it is the recommended path and
   * this is the way past it, which is the same relationship `defer` has to the
   * enrolment nudge and deliberately the same shape of sentence. Without it the
   * two controls read as a choice between equals, and the page would be asking a
   * question rather than making an offer.
   */
  usePasscode: 'Use an email code instead',

  /**
   * The passcode conversation (SYS-7) — three fields, one at a time.
   *
   * **The page's rule survives text inputs, and this is where to check it.**
   * ADR-0051 says the one control names the act it is about to perform; a form
   * whose submit button is generic would break that. So each step's button
   * names its own act, each has a `pending` word for the wait, and the field
   * above it is the only thing the person is being asked for.
   *
   * **The admin's script is the footer, not here.** Somebody who reaches the
   * `noAccess` message was told to come by a person, so the message sends them
   * back to that person rather than offering a self-service route that does not
   * exist (SYS-6, and SYS-9's third amendment for why we say it at all).
   */
  passcode: {
    email: {
      label: 'Work email address',
      hint: 'The address your agency administrator added.',
      submit: 'Send me a code',
      pending: 'Sending your code…',
      invalid: 'Enter your work email address.',
    },
    code: {
      /** `sentTo` is the address they typed — we echo it rather than Entra's masked form. */
      label: (sentTo: string) => `Enter the code we sent to ${sentTo}`,
      hint: 'It may take a minute to arrive. Check your junk folder.',
      submit: 'Sign in',
      pending: 'Checking your code…',
      invalid: 'Enter the code from the email.',
      resend: 'Send another code',
      resent: 'Another code is on its way.',
      /** Back to the address field — a typo is the commonest reason to be here. */
      change: 'Use a different address',
    },
    name: {
      title: 'What should we call you?',
      label: 'Your name',
      hint: 'This is how you appear to your agency and to the people you share with.',
      submit: 'Continue',
      pending: 'Saving…',
      invalid: 'Enter the name you would like to be shown by.',
    },
    /** The refusal SYS-9's third amendment argues for, said once and plainly. */
    fallback: 'Something went wrong. Try again.',
  },

  /**
   * What a credential enrolled from this page is called until somebody renames
   * it on the account screen (USR-11).
   *
   * The landing page does not ask for a name, and asking would put a text field
   * in the middle of a sign-in. It does not ask the shared-workstation question
   * either — it enrols in the own-device shape, which is safe because the offer
   * appears only where `platformAuthenticatorAvailable()` is true and ADR-0046
   * §3 has the server check that claim against the attestation rather than
   * trusting it. Somebody at a shared machine enrols from the account screen,
   * where the question is still asked.
   */
  passkeyLabel: 'This device',

  /**
   * `lead` renders in amber, bold — the one emphasis lead-in on the page, on
   * the template's own precedent. `rest` follows it in the paragraph's normal
   * (muted) tone. `lead` carries its own trailing space so the two concatenate
   * with no `{' '}` literal between them — JSX drops the whitespace-only text
   * node a line break would otherwise leave there.
   */
  support: {
    lead: 'Secure passwordless access. ',
    rest: 'Dedicated agency storage. Immutable audit history.',
  },

  features: [
    {
      title: 'Simple for users',
      body: 'No shared passwords, complicated setup, or special client software — sign in and send the file.',
    },
    {
      title: 'Isolated by agency',
      body: 'Each agency operates in its own dedicated storage boundary, with controlled access between connected organizations.',
    },
    {
      title: 'Verifiable custody',
      body: 'Transfers are integrity-checked and every action is written to an append-only audit history.',
    },
  ],

  footer: {
    help: 'Need access? Contact your agency administrator.',
    tag: 'Blue Partner · Secure government systems',
  },
} as const;
